import {
  BarChart3,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  HandCoins,
  GitMerge,
  LayoutDashboard,
  LineChart,
  ListChecks,
  Package,
  PackageCheck,
  ReceiptText,
  Truck,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  fetchPortalContext,
  getCachedPortalContext,
} from '../../services/portalContextService';

const navigationIcons = {
  overview: LayoutDashboard,
  'purchase-order': ClipboardList,
  'sales-order': ClipboardList,
  dumping: GitMerge,
  sales: ReceiptText,
  'cash-advance': Wallet,
  'sales-analytics': LineChart,
  inventory: Package,
  receiving: PackageCheck,
  releasing: Truck,
  supplier: Truck,
  lansa: ListChecks,
  customer: Users,
  reports: BarChart3,
  'daily-entries': CalendarDays,
  'initial-summary-po': ClipboardList,
  'supplier-balancing': ReceiptText,
};
const childNavigationIcons = {
  'cash-advance': Wallet,
  'supplier-savings': HandCoins,
  'manage-suppliers': Users,
  'lansa-name': ListChecks,
  'seaweed-type': Package,
  'warehouse-location': Truck,
  'tare-amount-fee': HandCoins,
  'savings-per-kilo': HandCoins,
};
const SIDEBAR_SCROLL_STORAGE_KEY = 'seaweeds.sidebar.scrollTop';
let savedSidebarScrollTop = getStoredSidebarScrollTop();

function Sidebar({
  navigationItems,
  activePath,
  activeSearch = '',
  navigationBadges = {},
  isCollapsed = false,
  version = 'v0.1.0',
  isMobileMenuOpen = false,
  onToggleCollapse,
  onCloseMobileMenu,
  onNavigate,
}) {
  const cachedPortalContext = getCachedPortalContext();
  const cachedBusinessName = cachedPortalContext?.business?.name?.trim();
  const cachedBusinessLogo = cachedPortalContext?.business?.brandLogo?.trim();
  const [businessName, setBusinessName] = useState(cachedBusinessName || 'Seaweeds');
  const [businessLogo, setBusinessLogo] = useState(cachedBusinessLogo || '');
  const [isBusinessContextLoading, setIsBusinessContextLoading] = useState(!cachedPortalContext);
  const navScrollRef = useRef(null);
  const hasRestoredScrollRef = useRef(false);
  const [expandedItemIds, setExpandedItemIds] = useState(() =>
    navigationItems
      .filter((item) => isItemOrChildActive(item, activePath, ''))
      .map((item) => item.id)
  );
  const [collapsedFlyoutItemId, setCollapsedFlyoutItemId] = useState('');
  const [showCollapsedScrollIndicator, setShowCollapsedScrollIndicator] = useState(false);

  useLayoutEffect(() => {
    const scrollElement = navScrollRef.current;

    if (!scrollElement) {
      return undefined;
    }

    scrollElement.scrollTop = savedSidebarScrollTop;
    hasRestoredScrollRef.current = true;

    const restoreFrame = window.requestAnimationFrame(() => {
      scrollElement.scrollTop = savedSidebarScrollTop;
    });

    return () => {
      window.cancelAnimationFrame(restoreFrame);
      preserveScrollPosition();
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function loadBusinessName() {
      try {
        const portalContext = await fetchPortalContext();
        const nextBusinessName = portalContext?.business?.name?.trim();
        const nextBusinessLogo = portalContext?.business?.brandLogo?.trim();

        if (isMounted && nextBusinessName) {
          setBusinessName(nextBusinessName);
        }

        if (isMounted && nextBusinessLogo) {
          setBusinessLogo(nextBusinessLogo);
        }
      } catch (error) {
        if (isMounted) {
          setBusinessName('Seaweeds');
          setBusinessLogo('');
        }
      } finally {
        if (isMounted) {
          setIsBusinessContextLoading(false);
        }
      }
    }

    loadBusinessName();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const activeItemWithChildren = navigationItems.find(
      (item) => isItemOrChildActive(item, activePath, activeSearch)
    );

    if (!activeItemWithChildren) {
      return;
    }

    setExpandedItemIds((currentIds) =>
      currentIds.includes(activeItemWithChildren.id)
        ? currentIds
        : [...currentIds, activeItemWithChildren.id]
    );
  }, [activePath, activeSearch, navigationItems]);

  useEffect(() => {
    if (!isCollapsed) {
      setCollapsedFlyoutItemId('');
    }
  }, [isCollapsed]);

  useEffect(() => {
    function updateScrollIndicator() {
      const scrollElement = navScrollRef.current;

      if (scrollElement && hasRestoredScrollRef.current) {
        savedSidebarScrollTop = scrollElement.scrollTop;
        storeSidebarScrollTop(savedSidebarScrollTop);
      }

      if (!scrollElement || !isCollapsed) {
        setShowCollapsedScrollIndicator(false);
        return;
      }

      const remainingScroll =
        scrollElement.scrollHeight - scrollElement.scrollTop - scrollElement.clientHeight;

      setShowCollapsedScrollIndicator(remainingScroll > 12);
    }

    updateScrollIndicator();

    const scrollElement = navScrollRef.current;

    if (!scrollElement) {
      return undefined;
    }

    scrollElement.addEventListener('scroll', updateScrollIndicator, { passive: true });
    window.addEventListener('resize', updateScrollIndicator);

    return () => {
      scrollElement.removeEventListener('scroll', updateScrollIndicator);
      window.removeEventListener('resize', updateScrollIndicator);
    };
  }, [expandedItemIds, isCollapsed, navigationItems]);

  function handleToggleItem(item) {
    setExpandedItemIds((currentIds) =>
      currentIds.includes(item.id)
        ? currentIds.filter((itemId) => itemId !== item.id)
        : [...currentIds, item.id]
    );
  }

  function handleToggleCollapsedFlyout(item) {
    setCollapsedFlyoutItemId((currentId) => (currentId === item.id ? '' : item.id));
  }

  function handleNavigate(nextPath) {
    setCollapsedFlyoutItemId('');
    onNavigate(nextPath);
  }

  function handleOpenItem(item) {
    setExpandedItemIds((currentIds) =>
      currentIds.includes(item.id) ? currentIds : [...currentIds, item.id]
    );
  }

  function preserveScrollPosition() {
    const scrollElement = navScrollRef.current;

    if (!scrollElement) {
      return;
    }

    savedSidebarScrollTop = scrollElement.scrollTop;
    storeSidebarScrollTop(savedSidebarScrollTop);
  }

  return (
    <>
      <div
        className={`fixed inset-0 z-30 bg-[#1f1a16]/35 backdrop-blur-[2px] transition-opacity md:hidden ${
          isMobileMenuOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={onCloseMobileMenu}
        aria-hidden="true"
      />
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex h-[100dvh] w-[340px] max-w-[92vw] flex-col border-r border-[var(--theme-border)] bg-white shadow-[18px_0_50px_rgba(var(--theme-shadow-rgb),0.16)] transition-transform duration-300 ease-out md:relative md:z-30 md:h-screen md:w-[230px] md:max-w-none md:translate-x-0 md:border-b-0 md:shadow-none md:transition-[width] md:duration-500 md:ease-[cubic-bezier(0.22,1,0.36,1)] ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ width: isCollapsed ? '96px' : undefined }}
        aria-label="Primary navigation"
      >
      <div
        className={`px-4 pb-0 pt-4 sm:px-5 sm:pt-5 md:transition-[padding,min-height] md:duration-500 md:ease-[cubic-bezier(0.22,1,0.36,1)] ${
          isCollapsed ? 'md:px-3 md:pt-2' : 'md:min-h-[96px]'
        }`}
      >
        <div
          className={`flex min-h-[68px] items-end border-b border-[var(--theme-border-soft)] pb-4 transition-[min-height,padding] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] sm:min-h-[76px] sm:pb-5 ${
            isCollapsed ? 'md:min-h-[56px] md:justify-center md:pb-1.5' : ''
          }`}
        >
          <div
            className={`flex w-full items-center gap-3 transition-[gap] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
              isCollapsed ? 'md:flex-col md:justify-center md:gap-0' : ''
            }`}
          >
            <div
              className={`grid h-11 w-11 shrink-0 place-items-center rounded-2xl border border-[var(--theme-border)] bg-[var(--theme-surface-soft)] shadow-[0_8px_22px_rgba(var(--theme-shadow-rgb),0.10),inset_0_1px_0_rgba(255,255,255,0.72)] ${
                isCollapsed ? 'md:translate-y-2' : ''
              }`}
            >
              {isBusinessContextLoading ? (
                <span
                  className="h-8 w-8 animate-pulse rounded-xl bg-[var(--theme-accent-soft)]"
                  aria-hidden="true"
                />
              ) : businessLogo ? (
                <img
                  className="h-8 w-8 rounded-xl object-cover"
                  src={businessLogo}
                  alt=""
                  aria-hidden="true"
                />
              ) : (
                <div className="grid gap-1" aria-hidden="true">
                  <span className="block h-1 w-5 rounded-full bg-[var(--theme-accent)]" />
                  <span className="ml-1.5 block h-1 w-5 rounded-full bg-[var(--theme-accent-strong)]" />
                  <span className="block h-1 w-5 rounded-full bg-[#d0aa67]" />
                </div>
              )}
            </div>
            <div
              className={`min-w-0 overflow-hidden transition-[max-width,opacity,transform] duration-300 ease-out lg:duration-400 ${
                isCollapsed
                  ? 'md:max-w-0 md:translate-x-1 md:opacity-0'
                  : 'md:max-w-[140px] md:translate-x-0 md:opacity-100'
              }`}
            >
              {isBusinessContextLoading ? (
                <div className="grid gap-2" aria-label="Loading business details">
                  <span className="h-4 w-32 animate-pulse rounded-full bg-[var(--theme-accent-soft)]" />
                  <span className="h-2.5 w-20 animate-pulse rounded-full bg-[var(--theme-border-soft)]" />
                </div>
              ) : (
                <>
                  <h1
                    className="m-0 truncate text-[1.12rem] font-semibold leading-tight tracking-[0.01em] text-[var(--theme-text)]"
                    title={businessName}
                  >
                    {businessName}
                  </h1>
                  <p className="m-0 mt-0.5 text-[0.72rem] font-semibold uppercase tracking-[0.12em] text-[var(--theme-text-muted)]">
                    Operations
                  </p>
                </>
              )}
            </div>
            <button
              className={`absolute right-[-22px] top-9 z-30 hidden h-11 w-11 items-center justify-center rounded-full border border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] shadow-[0_12px_30px_rgba(var(--theme-shadow-rgb),0.18)] transition-[right,transform,background-color,box-shadow] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] hover:bg-[var(--theme-surface-soft)] hover:scale-[1.02] md:inline-flex ${
                isCollapsed ? 'right-[-22px]' : ''
              }`}
              type="button"
              onClick={onToggleCollapse}
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-pressed={isCollapsed}
            >
              {isCollapsed ? (
                <ChevronRight className="h-5 w-5" aria-hidden="true" />
              ) : (
                <ChevronLeft className="h-5 w-5" aria-hidden="true" />
              )}
            </button>
            <button
              className="ml-auto inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] shadow-[0_8px_20px_rgba(var(--theme-shadow-rgb),0.10)] transition hover:bg-[var(--theme-surface-soft)] md:hidden"
              type="button"
              onClick={onCloseMobileMenu}
              aria-label="Close navigation menu"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      <div className="relative min-h-0 flex-1">
      <div
        ref={navScrollRef}
        className={`sidebar-scroll min-h-0 h-full overflow-y-auto px-4 pb-4 pt-3 sm:px-5 md:transition-[padding] md:duration-500 md:ease-[cubic-bezier(0.22,1,0.36,1)] ${
          isCollapsed ? 'sidebar-collapsed-scroll md:px-2 md:pt-2' : 'md:px-3'
        }`}
      >
      <nav
        className="grid gap-1"
        aria-label="Primary"
      >
        {navigationItems.map((item) => (
          <SidebarNavItem
            key={item.id}
            item={item}
            activePath={activePath}
            activeSearch={activeSearch}
            badgeCount={navigationBadges[item.id] || 0}
            badgeLabel={getNavigationBadgeLabel(item.id, navigationBadges[item.id] || 0)}
            isCollapsed={isCollapsed}
            isExpanded={expandedItemIds.includes(item.id)}
            isCollapsedFlyoutOpen={collapsedFlyoutItemId === item.id}
            onOpenItem={handleOpenItem}
            onPreserveScroll={preserveScrollPosition}
            onToggleCollapsedFlyout={handleToggleCollapsedFlyout}
            onToggleItem={handleToggleItem}
            onNavigate={handleNavigate}
          />
        ))}
      </nav>
      </div>
      {isCollapsed && showCollapsedScrollIndicator ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-2 hidden justify-center md:flex">
          <div className="grid h-8 w-8 place-items-center rounded-full border border-[var(--theme-border)]/95 bg-white/95 text-[var(--theme-text-muted)] shadow-[0_12px_28px_rgba(var(--theme-shadow-rgb),0.16)] backdrop-blur-[2px]">
            <ChevronDown className="h-4 w-4 animate-[bounce_1.8s_ease-in-out_infinite]" aria-hidden="true" />
          </div>
        </div>
      ) : null}
      </div>
      <div
        className={`border-t border-[var(--theme-border-soft)] px-4 py-3 text-[var(--theme-text-muted)] sm:px-5 md:transition-[padding] md:duration-500 md:ease-[cubic-bezier(0.22,1,0.36,1)] ${
          isCollapsed ? 'md:px-2' : 'md:px-3'
        }`}
      >
        <div
          className={`overflow-hidden rounded-xl border border-[var(--theme-border-soft)] bg-[var(--theme-surface-soft)] px-3 py-2 text-[0.7rem] font-semibold uppercase tracking-[0.12em] shadow-[0_6px_16px_rgba(var(--theme-shadow-rgb),0.08)] ${
            isCollapsed ? 'md:px-2 md:text-center' : ''
          }`}
          title={`Seaweeds ${version}`}
        >
          {isCollapsed ? version : `Version ${version.replace(/^v/i, '')}`}
        </div>
      </div>
      </aside>
    </>
  );
}

function SidebarNavItem({
  item,
  activePath,
  activeSearch,
  badgeCount = 0,
  badgeLabel = '',
  isCollapsed,
  isExpanded,
  isCollapsedFlyoutOpen,
  onOpenItem,
  onPreserveScroll,
  onToggleCollapsedFlyout,
  onToggleItem,
  onNavigate,
}) {
  const Icon = navigationIcons[item.id] ?? LayoutDashboard;
  const hasChildren = Boolean(item.children?.length);
  const activeChild = hasChildren
    ? getActiveChild(item, activePath, activeSearch)
    : null;
  const isActive = item.path === activePath || Boolean(activeChild);

  return (
    <div className="relative grid gap-0.5">
      <button
        className={`group relative flex w-full items-center justify-start gap-2 rounded-xl border border-transparent px-3 py-2.5 text-left font-inherit text-[0.9rem] font-medium text-[#2a2622] whitespace-nowrap transition-[padding,gap,background-color,border-color,box-shadow] duration-300 ease-out hover:border-[var(--theme-border)] md:text-[0.92rem] ${
          isCollapsed ? 'md:justify-center md:px-2 md:py-3' : 'md:py-2.5'
        } ${isActive ? 'border-[var(--theme-border)] bg-[var(--theme-accent-panel)] text-[var(--theme-accent-panel-text)] shadow-[inset_4px_0_0_var(--theme-accent),0_10px_24px_rgba(var(--theme-shadow-rgb),0.22)] hover:bg-[var(--theme-accent-panel)]' : 'bg-transparent hover:bg-[var(--theme-surface-soft)]'
        }`}
        type="button"
        onClick={() => {
          if (hasChildren) {
            if (isCollapsed) {
              onPreserveScroll();
              onToggleCollapsedFlyout(item);
              onOpenItem(item);
              onNavigate(getDefaultChildPath(item));
              return;
            }

            if (!isActive) {
              onPreserveScroll();
              onOpenItem(item);
              onNavigate(getDefaultChildPath(item));
              return;
            }

            onToggleItem(item);
            return;
          }

          onPreserveScroll();
          onNavigate(item.path);
        }}
        aria-current={isActive ? 'page' : undefined}
        aria-expanded={hasChildren ? isExpanded : undefined}
        title={isCollapsed ? item.label : undefined}
      >
        <span
            className={`grid h-7 w-7 shrink-0 place-items-center rounded-lg transition ${
            isActive ? 'bg-[var(--theme-accent)] shadow-[0_6px_14px_rgba(var(--theme-shadow-rgb),0.24)]' : 'bg-[var(--theme-surface-soft)] group-hover:bg-[var(--theme-accent-soft)]'
          }`}
          aria-hidden="true"
        >
          <Icon className={`h-[15px] w-[15px] ${isActive ? 'text-white' : 'text-[var(--theme-text-subtle)]'}`} />
        </span>
        <span
          className={`min-w-0 flex-1 overflow-hidden text-left transition-[max-width,opacity,transform] duration-200 ease-out md:duration-300 ${
            isCollapsed
              ? 'md:max-w-0 md:translate-x-1 md:opacity-0'
              : 'md:max-w-[140px] md:translate-x-0 md:opacity-100'
          }`}
        >
          {item.label}
        </span>
        {hasChildren ? (
          <ChevronDown
            className={`h-4 w-4 shrink-0 ${isActive ? 'text-[var(--theme-accent-panel-text)]' : 'text-[var(--theme-text-muted)]'} transition-[opacity,transform,max-width] duration-300 ease-out ${
              isExpanded ? 'rotate-180' : ''
            } ${
              isCollapsed
                ? 'md:max-w-0 md:translate-x-1 md:opacity-0'
                : 'md:max-w-4 md:translate-x-0 md:opacity-100'
            }`}
            aria-hidden="true"
          />
        ) : badgeCount > 0 ? (
          <NotificationBadge
            count={badgeCount}
            label={badgeLabel}
            isCollapsed={isCollapsed}
          />
        ) : null}
      </button>

      {hasChildren && isExpanded ? (
        <div
          className={`grid gap-0.5 overflow-hidden pl-6 transition-[max-height,opacity,transform] duration-300 ease-out ${
            isCollapsed
              ? 'md:max-h-0 md:translate-y-[-4px] md:opacity-0'
              : 'md:max-h-48 md:translate-y-0 md:opacity-100'
          }`}
        >
          {item.children.map((child) => {
            const isChildActive = child.id === activeChild?.id;
            const ChildIcon = childNavigationIcons[child.id] ?? ListChecks;

            return (
              <button
                key={child.id}
                className={`group/child flex items-center gap-2 rounded-lg px-3 py-1.5 text-left text-[0.8rem] font-medium transition ${
                  isChildActive
                    ? 'bg-[var(--theme-accent-panel)] text-[var(--theme-accent-panel-text)] shadow-[inset_3px_0_0_var(--theme-accent-strong)] hover:bg-[var(--theme-accent-panel)]'
                    : 'bg-transparent text-[var(--theme-text-subtle)] hover:bg-[var(--theme-surface-soft)]'
                }`}
                type="button"
                onClick={() => {
                  onPreserveScroll();
                  onOpenItem(item);
                  onNavigate(child.path);
                }}
                aria-current={isChildActive ? 'page' : undefined}
              >
                <span
                  className={`grid h-5 w-5 shrink-0 place-items-center rounded-md transition ${
                    isChildActive
                      ? 'bg-white/12 text-[var(--theme-accent-panel-text)]'
                      : 'bg-[var(--theme-surface-soft)] text-[var(--theme-text-subtle)] group-hover/child:bg-[var(--theme-accent-soft)] group-hover/child:text-[var(--theme-accent)]'
                  }`}
                  aria-hidden="true"
                >
                  <ChildIcon className="h-3.5 w-3.5" />
                </span>
                <span className="min-w-0 truncate">{child.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}

      {hasChildren && isCollapsed && (isCollapsedFlyoutOpen || isActive) ? (
        <div className="hidden justify-items-center gap-1.5 py-1 md:grid">
          {item.children.map((child) => {
            const isChildActive = child.id === activeChild?.id;
            const ChildIcon = childNavigationIcons[child.id] ?? ListChecks;

            return (
              <button
                key={child.id}
                className={`grid h-9 w-9 place-items-center rounded-xl border text-left transition ${
                  isChildActive
                    ? 'border-[var(--theme-border)] bg-[var(--theme-accent-panel)] text-[var(--theme-accent-panel-text)] shadow-[0_8px_18px_rgba(var(--theme-shadow-rgb),0.16)] hover:bg-[var(--theme-accent-panel)]'
                    : 'border-transparent bg-[var(--theme-surface-soft)] text-[var(--theme-text-subtle)] hover:border-[var(--theme-border)] hover:bg-[var(--theme-accent-soft)] hover:text-[var(--theme-accent)]'
                }`}
                type="button"
                title={child.label}
                onClick={() => {
                  onPreserveScroll();
                  onOpenItem(item);
                  onNavigate(child.path);
                }}
                aria-current={isChildActive ? 'page' : undefined}
              >
                <ChildIcon className="h-4 w-4" aria-hidden="true" />
                <span className="sr-only">{child.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function getDefaultChildPath(item) {
  return item.children?.[0]?.path || item.path;
}

function NotificationBadge({ count, label, isCollapsed }) {
  const displayLabel = count > 99 ? '99+' : String(count);
  const accessibleLabel = label || `${count} notification${count === 1 ? '' : 's'}`;

  return (
    <span
      className={`ml-auto inline-flex min-w-[22px] shrink-0 items-center justify-center rounded-full bg-[#c64f3d] px-1.5 py-0.5 text-[0.68rem] font-bold leading-none text-white shadow-[0_6px_14px_rgba(198,79,61,0.28)] transition-[max-width,opacity,transform] duration-300 ease-out ${
        isCollapsed
          ? 'md:absolute md:right-1.5 md:top-1.5 md:min-w-[18px] md:px-1 md:text-[0.58rem]'
          : ''
      }`}
      aria-label={accessibleLabel}
      title={accessibleLabel}
    >
      {displayLabel}
    </span>
  );
}

function getNavigationBadgeLabel(itemId, count) {
  if (itemId === 'purchase-order') {
    return `${count} received status`;
  }

  if (itemId === 'dumping') {
    return `${count} open dumping ${count === 1 ? 'record' : 'records'}`;
  }

  if (itemId === 'inventory') {
    return `${count} inventory ${count === 1 ? 'record' : 'records'} ready`;
  }

  if (itemId === 'releasing') {
    return `${count} sales ${count === 1 ? 'order' : 'orders'} ready to release`;
  }

  return `${count} notification${count === 1 ? '' : 's'}`;
}

function getStoredSidebarScrollTop() {
  if (typeof window === 'undefined') {
    return 0;
  }

  return Number(window.sessionStorage.getItem(SIDEBAR_SCROLL_STORAGE_KEY) || 0);
}

function storeSidebarScrollTop(scrollTop) {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.setItem(SIDEBAR_SCROLL_STORAGE_KEY, String(scrollTop));
}

function isItemOrChildActive(item, activePath, activeSearch) {
  return Boolean(item.children?.length) && (
    item.path === activePath ||
    Boolean(getActiveChild(item, activePath, activeSearch))
  );
}

function getActiveChild(item, activePath, activeSearch = '') {
  const children = item.children || [];
  const searchParams = new URLSearchParams(activeSearch);
  const activeTab = searchParams.get('tab');

  if (activeTab) {
    const tabChild = children.find((child) => {
      const childUrl = new URL(child.path, window.location.origin);
      const childTab = childUrl.searchParams.get('tab');

      return childTab === activeTab || child.id === activeTab;
    });

    if (tabChild) {
      return tabChild;
    }
  }

  return children.find((child) => {
    const childUrl = new URL(child.path, window.location.origin);

    return childUrl.pathname === activePath && childUrl.search === activeSearch;
  }) || children.find((child) => {
    const childUrl = new URL(child.path, window.location.origin);

    return childUrl.pathname === activePath && !childUrl.search;
  }) || (item.path === activePath ? children[0] : null);
}

export default Sidebar;
